package com.nonopn.skydome.common.app.api.impl

import com.nonopn.skydome.common.app.api.GeocodeResult
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.model.DashboardImpactReport
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.detection.ThunderDetection
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicDetail
import com.nonopn.skydome.common.app.pages.keraunic.KeraunicReport
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswer
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswerProjectWithThunderActivities
import com.nonopn.skydome.common.app.pages.main.model.api.ApiEstimatesData
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.SectionProjectAndThunderDetection
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.ProbabilityHolder
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import kotlinx.serialization.Serializable

@Suppress("TooManyFunctions")
sealed interface ApiInterface {
    val mode: Mode

    suspend fun probes(from: DateTimeTz, to: DateTimeTz): ApiAnswer

    suspend fun probes(from: DateTime, to: DateTime): ApiAnswer

    suspend fun updateProbe(probe: ThunderDetection, update: UpdateProbe): ThunderDetection

    suspend fun estimatesData(): List<ApiEstimatesData>

    suspend fun ng(coordinates: LatLng): NgComputation

    suspend fun geocoding(address: String): List<GeocodeResult>

    suspend fun certificateDensityActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ): List<SectionProjectAndThunderDetection>

    suspend fun certificateDensityActivities(
        from: DateTime,
        to: DateTime
    ): List<SectionProjectAndThunderDetection>

    suspend fun certificateActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ): List<ApiAnswerProjectWithThunderActivities>

    suspend fun certificateActivities(
        from: DateTime,
        to: DateTime
    ): List<ApiAnswerProjectWithThunderActivities>

    suspend fun downloadKeraunicReport(
        project: Project,
        from: DateTimeTz,
        to: DateTimeTz,
    ): ByteArray

    suspend fun downloadKeraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): ByteArray

    suspend fun downloadIncidenceReport(
        project: Project,
        instance: ThunderDetectionActivity
    ): ByteArray

    suspend fun downloadIncidenceReport(
        coordinates: LatLng,
        instance: ThunderDetectionActivity
    ): ByteArray

    suspend fun estimates(): List<ThunderDetectionEstimate>

    suspend fun createEstimate(request: EstimateRequest): ThunderDetectionEstimate

    suspend fun estimateCheckout(estimateUuid: String): EstimateCheckoutAnswer

    // GET /api/checkout/billing/portal?app= : url of the Stripe customer portal of the account
    suspend fun billingPortal(app: String): BillingPortalAnswer

    // Probe creation, 30-day trial and payment (lpsmanager /api/checkout/*, see docs/probe-creation-api.md)
    suspend fun checkoutProbes(request: ProbesCheckoutRequest): ProbesCheckoutAnswer

    suspend fun checkoutAccount(request: AccountCheckoutRequest): ProbesCheckoutAnswer

    suspend fun checkoutUpgrade(request: AccountCheckoutRequest): ProbesCheckoutAnswer

    // General state of the account for the app (GET /api/account/status), see docs/probe-creation-api.md
    suspend fun accountStatus(app: String): AccountStatus

    suspend fun keraunicDetail(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): ApiKeraunicDetail

    suspend fun keraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): KeraunicReport

    suspend fun dashboardProbeReport(): List<DashboardImpactReport>

    suspend fun dashboardProbeReportV2(): List<DashboardImpactReportV2>
}

@Suppress("TooManyFunctions")
object ApiInterfaceFake : ApiInterface {
    override val mode: Mode
        get() = Mode.Normal

    override suspend fun probes(from: DateTimeTz, to: DateTimeTz) = TODO("Not yet implemented")

    override suspend fun probes(from: DateTime, to: DateTime) = TODO("Not yet implemented")

    override suspend fun updateProbe(
        probe: ThunderDetection,
        update: UpdateProbe
    ) = TODO("Not yet implemented")

    override suspend fun estimatesData() = TODO("Not yet implemented")

    override suspend fun ng(coordinates: LatLng) = TODO("Not yet implemented")

    override suspend fun geocoding(address: String) = TODO("Not yet implemented")

    override suspend fun certificateDensityActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ) = TODO("Not yet implemented")

    override suspend fun certificateDensityActivities(
        from: DateTime,
        to: DateTime
    ) = TODO("Not yet implemented")

    override suspend fun certificateActivities(
        from: DateTimeTz,
        to: DateTimeTz
    ) = TODO("Not yet implemented")

    override suspend fun certificateActivities(
        from: DateTime,
        to: DateTime
    ) = TODO("Not yet implemented")

    override suspend fun downloadKeraunicReport(
        project: Project,
        from: DateTimeTz,
        to: DateTimeTz
    ) = TODO("Not yet implemented")

    override suspend fun downloadKeraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz
    ) = TODO("Not yet implemented")

    override suspend fun downloadIncidenceReport(
        project: Project,
        instance: ThunderDetectionActivity
    ) = TODO("Not yet implemented")

    override suspend fun downloadIncidenceReport(
        coordinates: LatLng,
        instance: ThunderDetectionActivity
    ) = TODO("Not yet implemented")

    override suspend fun estimates() = TODO("Not yet implemented")

    override suspend fun createEstimate(request: EstimateRequest) = TODO("Not yet implemented")

    override suspend fun estimateCheckout(estimateUuid: String): EstimateCheckoutAnswer {
        TODO("Not yet implemented")
    }

    override suspend fun billingPortal(app: String): BillingPortalAnswer {
        TODO("Not yet implemented")
    }

    override suspend fun checkoutProbes(request: ProbesCheckoutRequest): ProbesCheckoutAnswer =
        TODO("Not yet implemented")

    override suspend fun checkoutAccount(request: AccountCheckoutRequest): ProbesCheckoutAnswer =
        TODO("Not yet implemented")

    override suspend fun checkoutUpgrade(request: AccountCheckoutRequest): ProbesCheckoutAnswer =
        TODO("Not yet implemented")

    override suspend fun accountStatus(app: String): AccountStatus =
        TODO("Not yet implemented")

    override suspend fun keraunicDetail(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): ApiKeraunicDetail = TODO("Not yet implemented")

    override suspend fun keraunicReport(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): KeraunicReport = TODO("Not yet implemented")

    override suspend fun dashboardProbeReport(): List<DashboardImpactReport> =
        TODO("Not yet implemented")

    override suspend fun dashboardProbeReportV2(): List<DashboardImpactReportV2> =
        TODO("Not yet implemented")
}

@Serializable
data class NgComputation(
    val ng1month: Double,
    val ng3months: Double,
    val ng6months: Double,
    val ng1year: Double,
    val lastTimestamp: Long,
    val topIncidences: List<ThunderDetectionActivityWithIncidencePercent> = emptyList(),
) {
    @Serializable
    data class ThunderDetectionActivityWithIncidencePercent(
        val activity: ThunderDetectionActivity,
        val incidencePercent: ProbabilityHolder
    )
}

@Serializable
data class EstimateRequest(
    val uuid: String? = null,
    val email: String,
    val isSkySentinel: Boolean,
    val positions: List<LatLng>,
    val validUntilSecondsUtc: Long? = null,
    val areEnabled: Boolean = false,
)

@Serializable
data class ThunderDetectionEstimate(
    val uuid: String,
    val email: String,
    val probes: List<ThunderDetection>,
    val isSkySentinel: Boolean,
    val isTestStarting: Boolean,
    val isExternalContract: Boolean = false,
    val validUntilSecondsUtc: Long? = null,
    val areEnabled: Boolean,
    // Only filled by the admin estimates route (state, issues, payment type, subscription)
    val admin: AdminEstimateInfo? = null,
)

@Serializable
data class EstimateCheckoutAnswer(
    val redirectUrl: String,
)

@Serializable
data class BillingPortalAnswer(
    val redirectUrl: String,
)

/** Application of the lpsmanager account (value of the `App` enum of the API). */
object ProbeApp {
    const val STRIKE_RADAR = "StrikeRadar"
}

/** `ProbeToCreate` of the API: the name is required. */
@Serializable
data class ProbeToCreate(
    val latitude: Double,
    val longitude: Double,
    val name: String,
)

/** Body of POST /api/checkout/probes. */
@Serializable
data class ProbesCheckoutRequest(
    val app: String,
    val probes: List<ProbeToCreate>,
    val isDemo: Boolean? = null,
    val paymentProduct: String? = null,
    // Promotion code of the app, for a new subscription only (checked first with checkPromotionCode)
    val promotionCode: String? = null,
)

/** Body of POST /api/checkout/account and POST /api/checkout/upgrade. */
@Serializable
data class AccountCheckoutRequest(
    val app: String,
    val paymentProduct: String? = null,
    // Promotion code of the app, for a new subscription only (checked first with checkPromotionCode)
    val promotionCode: String? = null,
)

/**
 * Answer of the three checkout routes. paymentLink null = paid automatically by the active subscription;
 * demoLink is only set for a demo request.
 */
@Serializable
data class ProbesCheckoutAnswer(
    val estimateUuid: String = "",
    val probes: List<ThunderDetection> = emptyList(),
    val paymentLink: String? = null,
    val demoLink: String? = null,
)

@Serializable
data class UpdateProbe(
    val name: String,
)